import { CalculateStore } from "../../stores/calculateStore";
import { PackageId, PackageModel } from "../../helpers/package";
import { CalculationResponseModel } from "../../helpers/calculation";
import { RiskModel, RisksId } from "../../helpers/risk";
import Formatter from "../../helpers/formatter";
import { WidgetApiService } from "./widgetApiService";
import { RisksNetworkResponseModel } from "../../helpers/risksNetwork";
import { CalculationRequestMapper } from "./calculationRequestMapper";
import { RiskNetworkRequest, RisksNetworkRequestMapper } from "./risksNetworkRequestMapper";
import { CalculationResponseMapper } from "./calculationResponseMapper";
import { RequestStatus } from "./requestStatus";
import { runInAction } from "mobx";
import { ReplenishmentHistoryStore } from "../../stores/replenishmentHistoryStore";

export interface CalculationService {
  recalculatePackages: () => void;
  refreshPackage: (packageId: PackageId) => void;
  refreshPackageForceNewBiznes: (packageId: PackageId) => void;

  recalculate: (withPackages: boolean) => Promise<void>;
  recalculateAndStore: (handleError: (error: string) => void) => Promise<void>;
  setRisksNetwork: (packageData: PackageModel) => Promise<void>;
  setRisksNetworkForAllPackages: () => Promise<[PromiseSettledResult<void>, PromiseSettledResult<void>, PromiseSettledResult<void>]> | null;
}

export class DefaultCalculationService implements CalculationService {

  constructor(private calculateStore: CalculateStore,
    private widgetApiService: WidgetApiService,
    private calculationRequestMapper: CalculationRequestMapper,
    private risksNetworkRequestMapper: RisksNetworkRequestMapper,
    private calculationResponseMapper: CalculationResponseMapper,
    private replenishmentHistoryStore: ReplenishmentHistoryStore) {
  }

  public recalculatePackages() {
    let requireRecalculate = false;
    this.calculateStore.packages.forEach((packageData: PackageModel) => {
      if (packageData.requireRecalculate) {
        requireRecalculate = true;
      }
    });
    if (requireRecalculate) {
      this.calculateStore.packages.forEach((packageData: PackageModel) => {
        if (packageData.requireRecalculate) {
          this.refreshPackageAdapter(packageData.id, true);
          this.refreshPackage(packageData.id);
        }
      });

    }
  }

  public refreshPackage(packageId: PackageId) {
    this.refreshPackageAdapter(packageId, false);
  }

  public refreshPackageForceNewBiznes(packageId: PackageId) {
    if (packageId === PackageId.mini) {
      this.refreshPackageAdapter(packageId, true);
    }
  }

  private refreshPackageAdapter(packageId: PackageId, isNewBiznesForce: boolean) {
    const packageData = this.calculateStore.packages.get(packageId);
    if (!packageData) {
      return;
    }

    runInAction(() => {
      // TODO: create setter and remove 'runInAction'
      packageData.calculationResponse = undefined;
    });

    const response = this.calculationRequest(
      packageData.calculationRequestStatus, this.calculationRequestMapper.prepareRequestForPackage(packageData, isNewBiznesForce)
    );


    if (isNewBiznesForce) {
      this.setResponseInCache(response);
    } else {
      this.setResponseInStore(response, packageData.id);
    }
    this.requireRecalculateIfRefreshPackageSelected(packageId);
  }

  private setResponseInCache(response: Promise<any>) {
    response.then((resp) => {
      this.replenishmentHistoryStore.setResponseCache(
        {
          suggestionNumber: resp.coverages.houseOrFlatWithFixedElements?.insuranceSum
        }
      );
    }).catch((error) => {
      // tslint:disable-next-line:no-console
      console.log("Błąd: ", error);
    });
  }
  private requireRecalculateIfRefreshPackageSelected(packageId: PackageId) {
    if (packageId === this.calculateStore.selectedPackage) {
      this.calculateStore.setRequireRecalculate(true);
    }
  }

  private setResponseInStore(response: Promise<any>, packageId: PackageId) {
    response.then((resp) => {
      const packageData = this.calculateStore.packages.get(packageId);
      if (packageData) {
        runInAction(() => {
          // TODO: create setter and remove 'runInAction'
          packageData.calculationResponse = resp || null;
        });
      }
      this.updateInsuranceSumsFromResponse(packageId, resp);
      this.calculateStore.setRequireRecalculatePackage(packageId, false);

    }).catch((error) => {
      // tslint:disable-next-line:no-console
      console.log("Błąd: ", error);
    });
  }

  private updateInsuranceSumsFromResponse(packageId: PackageId, response: CalculationResponseModel) {
    if (packageId) {
      this.calculateStore.packages.get(packageId)?.risks?.forEach((risk: RiskModel, riskId: RisksId) => {
        if (risk.getInsuranceSumFromResponse) {
          let insuranceSum = risk.getInsuranceSumFromResponse(response);
          if (insuranceSum) {
            insuranceSum = Formatter.convertCentsToNormalNumber(insuranceSum);
          } else {
            insuranceSum = undefined;
          }
          if (risk.insuranceSum) {
            runInAction(() => {
              // TODO: create setter and remove 'runInAction'
              risk.insuranceSum = insuranceSum;
              if (!risk.suggestedInsuranceSum) {
                risk.suggestedInsuranceSum = insuranceSum;
              }
            });
          } else {
            runInAction(() => {
              // TODO: create setter and remove 'runInAction'
              risk.suggestedInsuranceSum = insuranceSum;
            });
          }
        }
      });
    }
  }

  public async recalculate(withPackages: boolean) {
    this.calculateStore.setRequireRecalculate(false);
    if (withPackages) {
      this.recalculatePackages();
    }

    const selectedPackageId = this.calculateStore!.selectedPackage;

    if (selectedPackageId && this.calculateStore.packages.has(selectedPackageId)) {
      const packageData = this.calculateStore.packages.get(selectedPackageId)!;

      const response = await this.calculationRequest(
        this.calculateStore.calculationRequestStatus, this.calculationRequestMapper.prepareRequestForSummaryBox(packageData)
      );
      this.calculateStore.setCalculationResponse(response);
      this.calculationResponseMapper.mapResponse(response);
    }
  }

  public async recalculateAndStore(handleError: (error: string) => void) {
    const selectedPackage = this.calculateStore.packages.get(this.calculateStore.selectedPackage)!;
    const request = this.calculationRequestMapper.prepareRequestForSummaryBox(selectedPackage, false);

    return this.saveCalculationRequest(request).then((response) => {
      if (response) {
        this.calculateStore.setCalculationResponse(response);
        this.calculateStore.setParentCalculationId(response.calculationId);
      }
    }).catch((error) => {
      handleError(error);
    });
  }

  private async saveCalculationRequest(data: any) {
    return await this.calculateStore.saveCalculationRequestStatus.handleRequest(
      () => this.widgetApiService.calculate(data)
    );
  }

  private async calculationRequest(requestStatus: RequestStatus, data: any): Promise<any> {
    const response = await requestStatus.handleRequest(
      () => this.widgetApiService.calculate(data)
    );

    return response;
  }

  public setRisksNetwork(packageData: PackageModel) {
    return new Promise<void>((resolve, reject) => {
      const request = this.risksNetworkRequestMapper.prepareRisksNetworkRequest(packageData.risks);
      const response = this.risksNetworkRequest(packageData, request);
      response.then((resp) => {
        this.updateRisksNetworkFromResponse(packageData.id, resp);
        resolve();
      }).catch((error) => {
        // tslint:disable-next-line:no-console
        console.log("Błąd: ", error);
        reject();
      });
    });
  }

  public setRisksNetworkForAllPackages() {
    const packages = this.calculateStore!.packages;
    if (packages.has(PackageId.mini) && packages.has(PackageId.midi) && packages.has(PackageId.maxi)) {
      return Promise.allSettled(
        [this.setRisksNetwork(packages.get(PackageId.mini)!),
        this.setRisksNetwork(packages.get(PackageId.midi)!),
        this.setRisksNetwork(packages.get(PackageId.maxi)!)]
      );
    }
    return null;
  }

  private async risksNetworkRequest(packageData: PackageModel, request: RiskNetworkRequest): Promise<RisksNetworkResponseModel> {
    const response = await packageData.riskNetworkRequestStatus.handleRequest(
      () => this.widgetApiService.risksNetwork(request)
    );

    return response;
  }

  private updateRisksNetworkFromResponse(packageId: PackageId, response: RisksNetworkResponseModel) {
    if (packageId) {
      const packageData = this.calculateStore.packages.get(packageId);
      packageData?.risks?.forEach((risk: RiskModel) => {
        if (risk.getRisksNetworkValue) {
          runInAction(() => {
            // TODO: create setter and remove 'runInAction'
            risk.risksNetworkValue = risk.getRisksNetworkValue!(response);
          });
        }
        if (packageData) {
          runInAction(() => {
            // TODO: create setter and remove 'runInAction'
            packageData.policyDuration = response.insuranceDuration;
          });
        }
      });
    }

    if (packageId === this.calculateStore.selectedPackage) {
      for (const additionalInsurancesKey in this.calculateStore.additionalInsurances) {
        if (this.calculateStore.additionalInsurances.hasOwnProperty(additionalInsurancesKey)) {
          // @ts-ignore
          const insurance = this.calculateStore.additionalInsurances[additionalInsurancesKey];
          if (insurance.getRisksNetworkValue) {
            runInAction(() => {
              // TODO: create setter and remove 'runInAction'
              insurance.risksNetworkValue = insurance.getRisksNetworkValue(response);
            });
          }
        }
      }
    }
  }
}
